import React from 'react';
import styled, { css } from 'styled-components';

import { useTypedSelector } from '@/store/store';
import { selectDealer } from '@/store/reducers/single-dealer';
import { Marker } from '@/typings/model';
import { ContentContainerSmall } from '@/components/ContentContainer';
import { colors } from '@/constants/theme';
import DealerCard from '@/modules/Dealers/components/DealerCard';
import DealersMap from '@/components/DealersMap';
import { media } from '@/utils/mixin';
import Rating from '@/components/Rating';
import { handleScrollToDomElement } from '@/utils/scroll';

function Hero() {
  const dealer = useTypedSelector(selectDealer);

  if (!dealer) {
    return null;
  }

  const markers = dealer.locations.reduce<Marker[]>(
    (acc, { name, address, latitude, longitude }) => {
      acc?.push({
        name: name,
        address: address,
        latitude: latitude ?? 0,
        longitude: longitude ?? 0,
      });
      return acc;
    },
    []
  );

  return (
    <Component>
      <ContentContainerSmall>
        <Header>
          <Title>{dealer.name}</Title>
          {dealer.reviewsCount > 0 ? (
            <Reviews onClick={() => handleScrollToDomElement('reviews')}>
              <Rating count={5} value={dealer.rating ?? 0} />
              <ReviewCount>{dealer.reviewsCount} отзывов</ReviewCount>
            </Reviews>
          ) : null}
        </Header>

        <Tabs>
          <Tab onClick={() => handleScrollToDomElement('services')}>
            Контакты и Сервисы
          </Tab>
        </Tabs>
      </ContentContainerSmall>

      <Content>
        <DealersMap markers={markers} />
        <ContentContainerSmall>
          <DealerCard
            name={dealer.name ?? ''}
            legalName={dealer.legalName ?? ''}
            logos={
              !dealer.hideLogos && dealer.brands && dealer.brands.length !== 0
                ? dealer.brands.map(({ logo }) => ({ url: logo ?? '' }))
                : []
            }
            locations={
              dealer.locations && dealer.locations.length !== 0
                ? dealer.locations.map((location) => ({
                    name:
                      dealer.locations.length > 1 ? location.name ?? '' : '',
                    address: location.address ?? '',
                    phones: location.phones,
                    socialYoutube: location.socialYoutube ?? '',
                    socialInstagram: location.socialInstagram ?? '',
                    socialOdnoklassnikiUrl:
                      location.socialOdnoklassnikiUrl ?? '',
                    socialFacebookUrl: location.socialFacebookUrl ?? '',
                    socialVkontakteUrl: location.socialVkontakteUrl ?? '',
                    socialTelegramUrl: location.socialTelegramUrl ?? '',
                    socialViberUrl: location.socialViberUrl ?? '',
                    socialSkypeUrl: location.socialSkypeUrl ?? '',
                    divisions:
                      location.divisions && location.divisions.length !== 0
                        ? location.divisions.map(({ name }) => name ?? '')
                        : [],
                  }))
                : []
            }
          />
        </ContentContainerSmall>
      </Content>
    </Component>
  );
}

export default Hero;

const Component = styled.section`
  padding-top: 60px;

  ${media.tablet(css`
    padding-top: 20px;
  `)}

  ${ContentContainerSmall} {
    ${media.tablet(css`
      padding: 0 20px;
    `)}
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;

  ${media.mobile(css`
    flex-direction: column;
    align-items: flex-start;
  `)}
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 40px;
  line-height: 52px;
  margin: 0;
  color: ${(props) => props.theme.black};

  ${media.tablet(css`
    font-size: 26px;
    line-height: 34px;
  `)}
`;

const Reviews = styled.span`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 20px;

  ${media.mobile(css`
    margin-left: 0;
    margin-top: 20px;
  `)}
`;

const ReviewCount = styled.span`
  font-size: 14px;
  line-height: 24px;
  margin-left: 10px;
  color: ${colors.main};
  border-bottom: 1px solid ${colors.main};
`;

const Tabs = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Tab = styled.span`
  cursor: pointer;
  padding: 30px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  color: ${colors.main};
  border-bottom: 1px solid ${colors.main};

  ${media.tabletSmall(css`
    flex: 1;
    padding: 20px;
  `)}
`;

const Content = styled.div`
  position: relative;
  padding: 60px 0;

  ${media.mobile(css`
    padding: 0;

    ${ContentContainerSmall} {
      padding: 0;
    }
  `)}
`;
